<template>
	<div>
		<div class="d-flex flex-row flex-wrap align-items-center mb-3">
			<h2 class="mr-auto">
				<template v-if="timetable">
					{{ timetable.name }}
				</template>
			</h2>
			<div v-if="timetable" class="d-flex flex-row align-items-center justify-content-end flex-wrap" style="gap: 0.5rem">
				<template v-if="!showFilters">
					<!-- Zoom -->
					<button class="btn btn-outline-secondary" @click="--zoom" :disabled="zoom<=1"><b-icon icon="zoom-out"></b-icon></button>
					<button class="btn btn-outline-secondary" @click="++zoom" :disabled="zoom>=3"><b-icon icon="zoom-in"></b-icon></button>

					<!-- Slim rows -->
					<button type="button" class="btn" :class="slimRows ? 'btn-primary' : 'btn-outline-secondary'" @click="slimRows = !slimRows">
						{{ $t('slim-rows') }}
					</button>

					<!-- Toggle filters -->
					<button
						class="btn"
						:class="showFilters ? 'btn-primary' : 'btn-outline-secondary'"
						@click="showFilters = true"
					>
						<b-icon icon="filter" class="mr-1" />{{ $t('filters') }}
					</button>

					<!-- Hide weekends -->
					<b-dropdown :text="$t('collapse-epsilon')">
						<b-dropdown-item :active="collapseWeekends" @click="toggleWeekends">
							{{ $t('weekends') }} <b-icon v-show="collapseWeekends" icon="check"/>
						</b-dropdown-item>
						<b-dropdown-item :active="collapseEmptyDates" @click="toggleEmptyDates">
							{{ $t('empty-dates') }} <b-icon v-show="collapseEmptyDates" icon="check"/>
						</b-dropdown-item>
					</b-dropdown>
				</template>

				<!-- Menus -->
				<template v-if="$UserSession.hasPriv.call(this, 'TIMETABLE_ADMIN')">
					<b-dropdown id="dropdown-1" :text="$t('menu')" class="ml-2">
						<b-dropdown-item :to="`/timetables/time/${$route.params['timetableid']}`">{{$t('view-time')}}</b-dropdown-item>
						<b-dropdown-item :to="`/timetables/roomusage/${$route.params['timetableid']}`">{{$t('view-room-usage')}}</b-dropdown-item>
						<b-dropdown-item :to="`/timetables/staff/${$route.params['timetableid']}`">{{$t('teachers')}}</b-dropdown-item>
						<b-dropdown-item :to="`/timetables/appointments/${$route.params['timetableid']}`">{{$t('appointments')}}</b-dropdown-item>
						<b-dropdown-item :to="`/timetables/import/${$route.params['timetableid']}`">{{$t('import')}}</b-dropdown-item>
						<b-dropdown-item :to="`/timetables/settings/${$route.params['timetableid']}`">{{$t('settings')}}</b-dropdown-item>
						<b-dropdown-item :to="`/timetables/edit/${$route.params.timetableid}${useBgEvents ? '' : '/backgroundevents'}`">
							{{  useBgEvents ? $t('return-to-events') : $t('view-background-events') }}
						</b-dropdown-item>
						<b-dropdown-item :to="`/timetables/${$route.params['timetableid']}/students`">{{ $t('invalid-tt-students') }}</b-dropdown-item>
						<b-dropdown-item @click="downloadEventsCSV">{{ $t('download-event-csv') }}</b-dropdown-item>
					</b-dropdown>

					<!-- New -->
					<b-dropdown variant="primary" :text="$t('new')">
						<b-dropdown-item :to="`/timetables/${timetable.id}/events/0`">{{ $t('new-event') }}</b-dropdown-item>
						<b-dropdown-item :to="`/timetables/${timetable.id}/backgroundevents/0`">{{ $t('new-bg-event') }}</b-dropdown-item>
					</b-dropdown>
				</template>
			</div>
		</div>

		<!-- Filter sidebar -->
		<div class="formeditor__sidebar"
			v-if="loaded"
			v-show="showFilters"
		>
			<div class="mb-2">
				<button class="btn btn-outline-secondary" @click="--zoom" :disabled="zoom<=1"><b-icon icon="zoom-out"></b-icon></button>
				<button class="btn btn-outline-secondary ml-2" @click="++zoom" :disabled="zoom>=3"><b-icon icon="zoom-in"></b-icon></button>
				<button type="button" class="btn ml-2" :class="slimRows ? 'btn-primary' : 'btn-outline-secondary'" @click="slimRows = !slimRows">
					{{ $t('slim-rows') }}
				</button>
				<button class="btn btn-light btn-xs float-right"
				   @click="showFilters = false"><b-icon icon="x" width="1em" height="1em"></b-icon></button>
			</div>
			<div class="mb-2">
				<button v-if="this.firm" type="button" class="btn ml-auto btn-outline-danger" @click="$router.replace({ query: { ...$route.query, firm: undefined }})">
					<b-icon icon="x" class="mr-1"/>{{ $t('firm') }} {{  $config.getValue('firms')[this.firm] }}
				</button>
			</div>
			<AdminFilters
				ref="filters"
				:timetable="timetable"
				:selectedYears="showYears"
				@updateYears="val => showYears = val"
				:allGroups="groups"
				:selectedGroups="showGroups"
				@updateGroups="val => showGroups = val"
				:allRooms="rooms"
				:selectedRooms="showRooms"
				@updateRooms="val => showRooms = val"
				:selectedUsers="showStudents"
				@updateUsers="val => showStudents = val"
				@searchRoomsOnDate="val => selectStudentsInRoomsOnDate(val)"
				@slimRows="val => slimRows = val"
			/>
		</div>

		<!-- Timetable -->
		<template v-if="loaded">
			<template v-for="programme in Object.values($config.getValue('programmes')).filter(pr => {
				return !$route.query.programme || pr.Id == $route.query.programme
			})">
				<AdminTimetable
					ref="timetables"
					v-if="groups.filter(gr => gr.programme_id == programme.Id).length"
					:key="programme.Id"
					:from="from"
					:until="until"
					:programme="programme"
					:events="useBgEvents ? undefined : events.filter(ev => ev.groups.findIndex(gr => gr.programme_id = programme.Id) > -1)"
					:bgEvents="useBgEvents ? bgEvents.filter(ev => ev.groups.findIndex(gr => gr.programme_id = programme.Id) > -1) : undefined"
					:years="showYears.filter(y => y.programme_id == programme.Id).map(y => y.year)"
					:groups="(showGroups.length ? showGroups : groups).filter(gr => gr.programme_id == programme.Id)"
					:rooms="showRooms"
					:firm="firm"
					:students="(showStudents.length ? showStudents : timetable.students.map(st => { return { ...st, id: st.user_id }}))"
					:absences="absences"
					:zoom="zoom"
					:slim="slimRows"
					:showViewToggleAlways="showYears.length + showGroups.length > 0"
					@selectUser="student => $router.push(`/timetables/individual/${student.id}/${timetable.id}`)"
					@selectEvent="selectEvent"
					@showYear="y => showYears = [{programme_id: programme.Id, year: y}]"
					@showGroup="g => showGroups = [g]"
					:allowEdit="$UserSession.hasPriv.call(this, 'TIMETABLE_ADMIN')"
					:timetable="timetable"
					:dense="collapseEmptyDates"
					:hideWeekends="collapseWeekends"

					:scrollPaginate="!useBgEvents && !disableScrollPaginate"
					:minDate="timetable.datefrom"
					:maxDate="timetable.dateto"
					@back="loadEarlier"
					@forward="loadLater"

					@saveState="() => { $refs.filters.saveState() }"
				/>
			</template>
		</template>

		<div v-else class="skeleton midnight h20 w100 mr-4 mb-3 float-left" v-for="n in 100" :key="n" />

		<EventDetailsModal
			v-if="selectedEvent"
			v-model="modalVisible"
			:event="selectedEvent"
		/>
	</div>
</template>

<script>
import Papa from 'papaparse';
import SessionManager from "src/modules/session/sessionManager"
import { dateToISODate, isoDateFromTimestamp, isoTimeFromTimestamp } from "src/modules/timetable/util"

import AdminTimetable from "src/components/Timetables/AdminTimetable.vue";
import AdminFilters from "src/components/Timetables/AdminFilters.vue";
import EventDetailsModal from 'src/components/Timetables/EventDetailsModal.vue';

export default {
	components: {
		AdminTimetable,
		AdminFilters,
		EventDetailsModal,
	},
	data() {
		return {
			zoom: 3,
			submitting: false,
			loaded: false,
			groups: [],
			days: [],
			timetable: null,
			from: null,
			until: null,
			sessiontypes: [],
			rooms: [],
			firm: null,
			programme: null,
			events: [],
			bgEvents: [],
			useBgEvents: this.$route.params.type === 'backgroundevents',
			disableScrollPaginate: this.$route.query.from && this.$route.query.to,
			studentsByUserId: {},
			absences: [],
			selectedEvent: null,
			modalVisible: false,

			// We can only process our events (attach their students and sessions) once our timetable and session types are fetched,
			// so we'll store the promise for those API requests in these vars.
			timetableLoaded: null,
			sessionTypesLoaded: null,
			eventsLoaded: null,

			// Filters
			showFilters: false,
			filterByGroups: false,
			showGroups: [],
			showYears: [],
			showRooms: [],
			showStudents: [],
			slimRows: false,
			collapseWeekends: false,
			collapseEmptyDates: false,
			filterFrom: this.$route.query.from,
			filterTo: this.$route.query.to,
			roomSearchDate: '',

			sessionManager: new SessionManager(this.$route)
		}
	},
	mounted() {
		const from = this.$route.query.from ? this.$route.query.from : this.sessionManager.get('from-date') ? this.sessionManager.get('from-date') : null
		const until = this.$route.query.to ? this.$route.query.to : null

		this.eventsLoaded = this.useBgEvents
		? this.fetchBackgroundEvents()
		: this.fetchEvents(from, until)

		this.disableScrollPaginate = this.$route.query.from && this.$route.query.to

		if (this.$route.query.from || this.$route.query.to) history.replaceState(null, '', this.$route.path.split('?')[0])

		if (this.$route.query.programme) this.programme = this.$config.getValue('programmes')[this.$route.query.programme]

		this.firm = this.$route.query.firm
		if (this.firm) delete this.$route.query.firm

		Promise.all([
			this.fetchTimetable(),
			this.fetchSessionTypes(),
			this.eventsLoaded,
		]).then(() => {
			this.loaded = true

			if (from > this.timetable.datefrom && !this.disableScrollPaginate) this.loadEarlier(from)
		})
	},
	methods: {
		fetchTimetable() {
			this.timetableLoaded = this.$cfetch(`${this.$settings.APIBaseURL}/timetables/get`, {
				data: {
					id: Number(this.$route.params["timetableid"])
				},
			}).then((res) => {
				if (res.status !== "SUCCESS") throw new Error()

				this.timetable = res.result
				this.from = this.timetable.datefrom
				this.until = this.timetable.dateto

				this.fetchAbsences()

				this.groups = res.result.groups.map(group => {
					if (!(group.programme_id && group.year && group.group_id)) return

					return {
						id: `${group.programme_id}_${group.year}_${group.group_id}`,
						group_id: group.group_id,
						programme_id: group.programme_id,
						year: group.year,
						title: this.$config.getValue('groups')[group.group_id]
					}
				}).filter(group => group !== undefined)

				this.studentsByUserId = this.timetable.students.reduce((prev, curr) => {
					prev[curr.user_id] = {
						...curr,
						id: curr.user_id,
					}
					return prev
				}, {})

				this.timetableLoaded = Promise.resolve(true)
			}).catch(err => {
				console.error(err)
				this.$log.error.call(this, err)
				this.error = this.$t("failed-load-timetable")
				this.$router.push('/')
			})

			return this.timetableLoaded
		},
		fetchSessionTypes() {
            this.sessionTypesLoaded = this.$cfetch(`${this.$settings.APIBaseURL}/timetables/sessiontypes/all`, {
                method: 'GET',
            }).then(res => {
                if (res.status !== 'SUCCESS') throw new Error()

                this.sessiontypes = res.results
            }).catch(err => {
                console.error(err)
                this.$log.error.call(this, err)
                this.$store.commit('openDialog', {
                    title: this.$t('something-went-wrong'),
                    content: this.$t('error-fetching-session-types'),
                })
            })

			return this.sessionTypesLoaded
        },
		async fetchAbsences() {
			return this.$cfetch(`${this.$settings.APIBaseURL}/absences/all`, {
				method: 'GET',
				data: {
					from: this.from,
					to: this.until,
				}
			}).then(res => {
				if (res.status !== 'SUCCESS') throw Error()

				this.absences = res.results
			}).catch(err => {
				this.$log.error.call(this, err)
			})
		},
		/**
		 * Fetches events within the date range.
		 *
		 * Starts at timetable start if no 'from' passed.
		 *
		 * The endpoint returns a 4 week period in absence of a 'to' date given.
		 *
		 * @param {String} from YYYY-MM-DD
		 */
		fetchEvents(from=null, to=null) {
			if (this.submitting) return
			this.submitting = true

			// If the user navigates away while this function recurses, stop
			if (this.$route.name !== 'timetable-edit') return Promise.resolve()

			return this.$cfetch(`${this.$settings.APIBaseURL}/timetables/events/all`, {
				data: {
					timetable_id: Number(this.$route.params.timetableid),
					from: from ? from : undefined,
					to: to ? to : undefined,
				},
			}).then((res) => {
				if (res.status !== 'SUCCESS') throw new Error()

				// processEvents() requires timetable and session type data to be loaded,
				// so we'll make sure the API responses for each have resolved.
				Promise.all([this.timetableLoaded, this.sessionTypesLoaded]).then(() => {
					this.events = this.processEvents(res.results)
					this.from = res.from > this.timetable.datefrom ? res.from : this.timetable.datefrom
					this.until = res.to < this.timetable.dateto ? res.to : this.timetable.dateto

					this.sessionManager.set('from-date', this.from)
				})
			}).catch(err => {
				console.error(err)
				this.$log.error.call(this, err)
				this.$store.commit('openDialog', {
					title: this.$t('something-went-wrong'),
					content: this.$t('error-fetching-event'),
				})
			}).finally(() => {
				this.submitting = false
			})
		},
		fetchBackgroundEvents() {
			return this.$cfetch(`${this.$settings.APIBaseURL}/timetables/backgroundevents/all`, {
				data: {
					timetable_id: Number(this.$route.params["timetableid"]),
				}
			}).then(res => {
				if (res.status !== 'SUCCESS') throw new Error()

				this.bgEvents = res.results.map(event => {
					event.groups = event.groups.map(gr => {
						return {
							...gr,
							id: `${gr.programme_id}_${gr.year}_${gr.group_id}`
						}
					})
					return event
				})
			}).catch(err => {
				console.error(err)
				this.$log.error.call(this, err)
				this.$store.commit('openDialog', {
					title: this.$t('something-went-wrong'),
					content: this.$t('error-fetching-bg-events'),
				})
			})
		},
		selectEvent(event) {
			if (this.$UserSession.hasPriv('TIMETABLE_ADMIN')) {
				const date = this.$refs.timetables[0].getLeftOfScreenDay().id.split('-').map((v, i) => {
					v = i === 1 ? Number(v) + 1 : Number(v)
					if (i > 0) {
						return v < 10 ? `0${v}` : `${v}`
					}
					return `${v}`
				}).join('-')

				this.sessionManager.set('from-date', date)
				this.$router.push(`/timetables/${this.timetable.id}/${this.useBgEvents ? 'backgroundevents' : 'events'}/${event.id}`)
			}
			else {
				this.selectedEvent = event
				this.modalVisible = true
			}
		},
		/**
		 * Attaches timetabled students, sessions and rooms to each event
		 *
		 * @param {Array<object>} events
		 *
		 * @returns {Array<object>} An array of processed events
		 */
		processEvents(events) {
			if (!events.length) return []

			// Let's map session types and rooms by ID
			const roomsById = {}
			const sessionTypesById = this.sessiontypes.reduce((prev, curr) => {
				curr.ttrooms.forEach(room => roomsById[room.id] = room)
				prev[curr.id] = curr
				return prev
			}, {})

			this.rooms = Object.values(roomsById)

			// For each event, collapse its students into an array of its unique groups
			// And use the maps to attach its session type / room
			events = events.map(event => {
				const students = []

				event.students.forEach(user => {
					const s = this.studentsByUserId[user.id]
					if (s) students.push(s)
				})

				event.students = students

				const key = (student) => `${student.programme_id}-${student.year}-${student.group_id}`

				event.groups = Object.values(event.students.reduce((prev, curr) => {
					prev[key(curr)] = {
						programme_id: curr.programme_id,
						year: curr.year,
						group_id: curr.group_id,
						id: `${curr.programme_id}_${curr.year}_${curr.group_id}`,
					}
					return prev
				}, {}))

				event.ttsessiontype = sessionTypesById[event.ttsessiontype_id]
				event.ttroom = roomsById[event.ttroom_id]

				return event
			})

			return events
		},
		/**
		 * @param {string} date YYYY-MM-DD
		 */
		loadEarlier(date) {
			if (this.submitting || date < this.timetable.datefrom) return Promise.resolve()

			let from = new Date(...date.split('-').map((val, i) => i === 1 ? Number(val)-1 : Number(val)))
			from.setDate(from.getDate() - 28) // We're doing 4-week chunks
			return this.fetchEvents(dateToISODate(from))
		},
		/**
		 * @param {string} date YYYY-MM-DD
		 */
		loadLater(date) {
			if (this.submitting || date > this.timetable.dateto) return Promise.resolve()

			let from = new Date(...date.split('-').map((val, i) => i === 1 ? Number(val)-1 : Number(val)))
			from.setDate(from.getDate() + 1)
			return this.fetchEvents(dateToISODate(from))
		},
		/**
		 * @param {string} date YYYY-MM-DD
		 */
		async selectStudentsInRoomsOnDate(date) {
			this.$refs.timetables.forEach(tt => tt.clearEvents())
			await this.fetchEvents(date)
			this.loadEarlier(date)

			this.showStudents = []

			this.$refs.timetables.forEach(tt => {
				const ids = tt.eventContainer.getStudentsWhere((key) => {
					return key === date
				}, (events) => {
					for (const event of events) {
						if (!event.ttroom_id) return false
						for (const room of this.showRooms) if (room.id == event.ttroom_id) return true
					}
					return false
				})

				for (const id of ids) this.showStudents.push(this.studentsByUserId[id])

				tt.studentView = true
			})

			if (!this.showStudents.length) return

			this.showFilters = false
			this.showRooms = []
			this.showYears = []
			this.showGroups = []
		},
		loadCurrentWeek() {
			const today = dateToISODate(new Date)
			this.$router.push(`/timetables/edit/${this.timetable.id}?from=${today}`)
		},
		downloadEventsCSV() {
			if (this.submitting) return
			this.submitting = true

			// If the user navigates away while this function recurses, stop
			if (this.$route.name !== 'timetable-edit') return Promise.resolve()

			return this.$cfetch(`${this.$settings.APIBaseURL}/timetables/teachers/events/all`, {
				data: {
					timetable_id: Number(this.$route.params.timetableid),
				},
			}).then(async (res) => {
				if (res.status !== 'SUCCESS') throw new Error()

				const teachernames = await this.$users.getTeachers.call(this).then(teachers => {
					return teachers.reduce((prev, curr) => {
						prev[curr.id] = curr.displayName
						return prev
					}, {})
				});
				const sessionnames = this.sessiontypes.reduce((prev, curr) => {
					prev[curr.id] = curr.name
					return prev
				}, {})
				const roomnames = this.rooms.reduce((prev, curr) => {
					prev[curr.id] = curr.name
					return prev
				}, {})

				const fields = {
					'title': this.$t('title'),
					'date': this.$t('date'),
					'from': this.$t('from'),
					'to': this.$t('to'),
					'ttsessiontype_name': this.$t('session-type'),
					'ttroom_name': this.$t('room'),
					'teachers': this.$t('teachers'),
				}

				const data = []

				res.results.forEach(event => {
					const datum = {
						teachers: `${event.teachers.map(teacher => {
							return teachernames[teacher.id]
						}).join(', ')}`,
						date: isoDateFromTimestamp(event.date),
						from: isoTimeFromTimestamp(event.from),
						to: isoTimeFromTimestamp(event.to),
						title: event.title,
						ttsessiontype_name: sessionnames[event.ttsessiontype_id],
						ttroom_name: roomnames[event.ttroom_id],
					}

					console.log(event.title, event)

					data.push(Object.keys(fields).map(field => datum[field]))
				})

				const csv = 'data:text/csv;charset=utf-8,' + Papa.unparse({ fields: Object.values(fields), data })
				const el = document.createElement('a')

				el.href = csv
				el.setAttribute('download', 'events.csv')
				el.click()
			}).catch(err => {
				console.error(err)
				this.$log.error.call(this, err)
				this.$store.commit('openDialog', {
					title: this.$t('something-went-wrong'),
					content: this.$t('error-fetching-bg-events'),
				})
			}).finally(() => {
				this.submitting = false
			})
		},
		toggleWeekends() {
			this.collapseEmptyDates = false
			this.collapseWeekends = !this.collapseWeekends

			if (!this.collapseWeekends) return


		},
		toggleEmptyDates() {
			this.collapseWeekends = false
			this.collapseEmptyDates = !this.collapseEmptyDates

			if (!this.collapseEmptyDates) return
		},
	},
	computed: {
		canEditBgEvents() {
			return this.$UserSession.hasPriv.call(this, 'TIMETABLE_ADMIN') || this.$UserSession.hasPriv.call(this, 'TTBGEVENT_OWN_EDIT')
		},
		todayInTimetable() {
			const date = new Date
			const todayStr = `${date.getFullYear()}-${date.getMonth()+1}-${date.getDate()}`
			return this.timetable && this.timetable.datefrom <= todayStr && todayStr <= this.timetable.dateto
		}
	}
}
</script>

<style>
th, td {
	padding: 2px !important;
	min-width: 50px;
}
</style>
